/**
 * Spec 028: money text per currency. USD keeps today's strings exactly
 * ("$1,234.50", never a code); CAD also shows "$" (en-CA) and adds " CAD" where
 * the code matters (totals, charges, emails; spec D14).
 */

export type Currency = 'USD' | 'CAD';

/** Reads a stored currency; anything but CAD is USD (rows from before spec 028). */
export const asCurrency = (value: unknown): Currency =>
  String(value ?? '').toUpperCase() === 'CAD' ? 'CAD' : 'USD';

const formatters: Record<Currency, Intl.NumberFormat> = {
  USD: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }),
  CAD: new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }),
};

export interface MoneyOptions {
  /** Adds " CAD" after a CAD amount. USD never shows a code. */
  withCode?: boolean;
}

export const formatMoney = (
  amount: number | null | undefined,
  currency: Currency | string | null | undefined,
  options: MoneyOptions = {},
): string => {
  const code = asCurrency(currency);
  const text = formatters[code].format(Number(amount) || 0);
  return options.withCode && code === 'CAD' ? `${text} CAD` : text;
};

/** The lower-case code Stripe expects. */
export const stripeCurrency = (currency: Currency | string | null | undefined): string =>
  asCurrency(currency).toLowerCase();

/**
 * Spec 028 D14: the code after a total. Empty for USD, so US text is unchanged;
 * " CAD" for CAD.
 */
export const currencyCodeSuffix = (currency: Currency | string | null | undefined): string =>
  asCurrency(currency) === 'CAD' ? ' CAD' : '';
